import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { JobGrade } from '../../lib/grading.ts';
import type { Submission } from '../../lib/types.ts';

interface CorpusJobListProps {
  rows: readonly CorpusJobRow[];
}

export interface CorpusJobRow {
  submission: Submission;
  grade: JobGrade;
}

type SortKey = 'name' | 'score' | 'graded' | 'promoted';
type SortDir = 'asc' | 'desc';

export function CorpusJobList({ rows }: CorpusJobListProps) {
  const [sortKey, setSortKey] = useState<SortKey>('promoted');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sorted = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);

  function setSort(key: SortKey): void {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }

  return (
    <div style={cardStyle}>
      <div style={labelStyle}>CORPUS JOBS</div>
      {sorted.length === 0 ? (
        <div style={{ color: '#525870', fontSize: 13, marginTop: 12 }}>
          No corpus jobs.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 12 }}>
          <thead>
            <tr style={headRowStyle}>
              <ColHeader label="Job" k="name" sortKey={sortKey} sortDir={sortDir} onSort={setSort} align="left" />
              <ColHeader label="Score %" k="score" sortKey={sortKey} sortDir={sortDir} onSort={setSort} />
              <ColHeader label="Graded" k="graded" sortKey={sortKey} sortDir={sortDir} onSort={setSort} />
              <ColHeader label="Promoted" k="promoted" sortKey={sortKey} sortDir={sortDir} onSort={setSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.map(({ submission, grade }) => (
              <tr key={submission.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '10px 8px' }}>
                  <Link
                    to={`/app/jobs/${submission.id}/output`}
                    style={{ color: '#F0F2FF', display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <span style={{ fontWeight: 600 }}>{submission.job_name}</span>
                    <span style={subtleMonoStyle}>{submission.source_pdf}</span>
                  </Link>
                </td>
                <td style={{ ...tdStyle, color: scoreColor(grade.scorePct), fontWeight: 700 }}>
                  {grade.scorePct.toFixed(1)}
                </td>
                <td style={tdStyle}>
                  {grade.totalGraded}
                  <span style={{ color: '#525870', marginLeft: 6 }}>
                    /{grade.pageGrades.length}
                  </span>
                </td>
                <td style={tdStyle}>{formatDate(submission.ground_truth?.promoted_at ?? null)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ColHeader({
  label,
  k,
  sortKey,
  sortDir,
  onSort,
  align,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = k === sortKey;
  const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '';
  return (
    <th
      onClick={() => onSort(k)}
      style={{
        ...thStyle,
        textAlign: align ?? 'right',
        cursor: 'pointer',
        color: active ? '#F0F2FF' : '#525870',
      }}
    >
      {label} {arrow}
    </th>
  );
}

function sortRows(
  rows: readonly CorpusJobRow[],
  key: SortKey,
  dir: SortDir,
): readonly CorpusJobRow[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    const cmp = compareBy(a, b, key);
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}

function compareBy(a: CorpusJobRow, b: CorpusJobRow, key: SortKey): number {
  switch (key) {
    case 'name':
      return a.submission.job_name.localeCompare(b.submission.job_name);
    case 'score':
      return a.grade.scorePct - b.grade.scorePct;
    case 'graded':
      return a.grade.totalGraded - b.grade.totalGraded;
    case 'promoted': {
      const av = a.submission.ground_truth?.promoted_at ?? '';
      const bv = b.submission.ground_truth?.promoted_at ?? '';
      return av < bv ? -1 : av > bv ? 1 : 0;
    }
  }
}

function scoreColor(pct: number): string {
  if (pct >= 90) return '#00E87A';
  if (pct >= 70) return '#C49BFF';
  if (pct >= 50) return '#FFD56B';
  return '#FF6B7A';
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}

const cardStyle: React.CSSProperties = {
  padding: 18,
  background: '#0D0F1C',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10,
};

const labelStyle: React.CSSProperties = {
  color: '#525870',
  fontSize: 10,
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
};

const headRowStyle: React.CSSProperties = {
  textAlign: 'right',
  color: '#525870',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 10,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
};

const thStyle: React.CSSProperties = {
  padding: '8px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  fontWeight: 600,
  userSelect: 'none',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 8px',
  textAlign: 'right',
  fontFamily: "'JetBrains Mono', monospace",
  color: '#B8BDD6',
};

const subtleMonoStyle: React.CSSProperties = {
  color: '#525870',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 10,
};
